import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useFetch, PageHeader } from "../../components/crm/ui";
import { Wrench, AlertTriangle, CheckCircle2, CalendarDays, CalendarRange, TrendingUp, Users } from "lucide-react";
import {
  TICKET_TYPE_LABEL, CLOSED_STATUSES, ticketCompletionPct, isTicketInProblem, monthKey, monthLabel,
} from "../../lib/ticketUtils";
import { SectionCard, KpiChip, ChartCard, MiniPie, MiniBar, SECTION_ACCENTS } from "../crm/dashboard/dashboardUi";

const C = SECTION_ACCENTS.ticket.chart;
const STATUS_COLOR = { open: C[0], in_progress: C[1], on_hold: C[2], resolution_support_required: C[3], completed: C[4], closed: C[5] };

export default function ServiceDashboard() {
  const navigate = useNavigate();
  const { data: ticketsRaw } = useFetch("/service-tickets");
  const { data: techniciansRaw } = useFetch("/technicians");
  const tickets = useMemo(() => ticketsRaw || [], [ticketsRaw]);
  const technicians = useMemo(() => techniciansRaw || [], [techniciansRaw]);
  const techName = useMemo(() => Object.fromEntries(technicians.map((t) => [t.id, t.name])), [technicians]);

  const insights = useMemo(() => {
    const now = new Date();
    const mtdKey = monthKey(now.toISOString());
    const yearStart = `${now.getFullYear()}`;

    let mtdCount = 0, ytdCount = 0, closedCount = 0, problemCount = 0, breachedCount = 0;
    let completionSum = 0;
    const byStatus = {};
    const byType = {};
    const byPriority = {};
    const byMonth = {};
    const byTech = {};

    for (const t of tickets) {
      const ck = monthKey(t.created_at);
      if (ck === mtdKey) mtdCount++;
      if (ck && ck.startsWith(yearStart)) ytdCount++;
      if (CLOSED_STATUSES.includes(t.status)) closedCount++;
      if (isTicketInProblem(t)) problemCount++;
      if (t.sla_breached) breachedCount++;
      completionSum += ticketCompletionPct(t);

      byStatus[t.status] = (byStatus[t.status] || 0) + 1;
      byType[t.type] = (byType[t.type] || 0) + 1;
      byPriority[t.priority || "normal"] = (byPriority[t.priority || "normal"] || 0) + 1;
      if (ck) byMonth[ck] = (byMonth[ck] || 0) + 1;

      const techId = t.technician_id || "unassigned";
      byTech[techId] = byTech[techId] || { id: techId, name: techId === "unassigned" ? "Unassigned" : (techName[techId] || `Unknown (${techId.slice(0, 6)})`), total: 0, completed: 0, open: 0, breached: 0, completionSum: 0 };
      byTech[techId].total++;
      if (CLOSED_STATUSES.includes(t.status)) byTech[techId].completed++; else byTech[techId].open++;
      if (t.sla_breached) byTech[techId].breached++;
      byTech[techId].completionSum += ticketCompletionPct(t);
    }

    const total = tickets.length || 1;
    const pctClosure = Math.round((closedCount / total) * 100);
    const avgCompletion = Math.round(completionSum / total);

    const statusData = Object.entries(byStatus).map(([name, value]) => ({ name: name.replace(/_/g, " "), value, color: STATUS_COLOR[name] || "#94A3B8" }));
    const typeData = Object.entries(byType).map(([k, value]) => ({ name: TICKET_TYPE_LABEL[k] || k, value }));
    const priorityData = Object.entries(byPriority).map(([name, value]) => ({ name, value }));
    const months = Object.keys(byMonth).sort().slice(-6);
    const trendData = months.map((m) => ({ month: monthLabel(m), value: byMonth[m] }));
    const techRows = Object.values(byTech)
      .map((r) => ({ ...r, avgCompletion: Math.round(r.completionSum / r.total) }))
      .sort((a, b) => b.total - a.total);

    return {
      total: tickets.length, mtdCount, ytdCount, pctClosure, problemCount, breachedCount, avgCompletion,
      statusData, typeData, priorityData, trendData, techRows,
    };
  }, [tickets, techName]);

  return (
    <div data-testid="service-dashboard-page">
      <PageHeader overline="Field Operations · Executive Report" title="Service Dashboard" />

      <SectionCard accent="ticket" testid="service-dashboard-kpis-card">
        <div className="flex items-center gap-1 flex-wrap mb-3" data-testid="service-dashboard-kpis">
          <KpiChip label="Total Tickets" value={insights.total} tone="bg-[#B91C1C] text-white" to="/service/tickets" />
          <KpiChip label="MTD" value={insights.mtdCount} />
          <KpiChip label="YTD" value={insights.ytdCount} />
          <KpiChip label="% Closure" value={`${insights.pctClosure}%`} tone="bg-emerald-50 text-emerald-800" to="/service/tickets?status=completed" />
          <KpiChip label="Avg Completion" value={`${insights.avgCompletion}%`} />
          <KpiChip label="Tickets In Problem" value={insights.problemCount} tone="bg-red-50 text-red-700" to="/service/tickets?problem=true" />
          <KpiChip label="SLA Breached" value={insights.breachedCount} tone="bg-amber-50 text-amber-800" to="/service/tickets?breached=true" />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="service-dashboard-charts">
          <ChartCard title="Tickets by Status" testid="chart-status">
            <MiniPie data={insights.statusData} colors={insights.statusData.map((d) => d.color)} to="/service/tickets" />
          </ChartCard>
          <ChartCard title="Tickets by Type" testid="chart-type">
            <MiniPie data={insights.typeData} colors={C} to="/service/tickets" />
          </ChartCard>
          <ChartCard title="Monthly Trend (6mo)" testid="chart-trend">
            <MiniBar data={insights.trendData} color={C[0]} to="/service/tickets" />
          </ChartCard>
          <ChartCard title="Tickets by Priority" testid="chart-priority">
            <MiniBar data={insights.priorityData} color={C[1]} xKey="name" to="/service/tickets" />
          </ChartCard>
        </div>
      </SectionCard>

      {/* Technician-wise analytics */}
      <div className="border border-[#E2E4DC] bg-white border-t-[3px] border-t-red-500 mt-4">
        <div className="px-4 py-2.5 border-b border-[#E2E4DC] bg-[#F4F6F1] font-mono text-[10px] uppercase tracking-widest text-[#1F4E2C] flex items-center gap-1.5">
          <Users className="w-3.5 h-3.5" /> Technician-wise Ticket Analytics
        </div>
        <table className="w-full text-[12px]" data-testid="technician-analytics-table">
          <thead>
            <tr className="text-left font-mono text-[9.5px] uppercase tracking-[0.2em] text-[#4A5568] border-b border-[#E2E4DC]">
              <th className="px-3 py-2.5">Technician</th>
              <th className="px-3 py-2.5 text-right">Total</th>
              <th className="px-3 py-2.5 text-right">Completed</th>
              <th className="px-3 py-2.5 text-right">Open</th>
              <th className="px-3 py-2.5 text-right">Avg Completion</th>
              <th className="px-3 py-2.5 text-right">SLA Breaches</th>
            </tr>
          </thead>
          <tbody>
            {insights.techRows.map((r) => (
              <tr
                key={r.id}
                className="border-b border-[#EEF2EA] hover:bg-[#F4F6F1] cursor-pointer transition-colors"
                data-testid={`tech-analytics-row-${r.id}`}
                onClick={() => navigate(r.id === "unassigned" ? "/service/tickets" : `/service/tickets?technician=${r.id}`)}
              >
                <td className="px-3 py-2 font-medium text-[#0F1A12]">{r.name}</td>
                <td className="px-3 py-2 text-right font-mono">{r.total}</td>
                <td className="px-3 py-2 text-right font-mono text-emerald-700">{r.completed}</td>
                <td className="px-3 py-2 text-right font-mono text-amber-700">{r.open}</td>
                <td className="px-3 py-2 text-right font-mono">{r.avgCompletion}%</td>
                <td className="px-3 py-2 text-right font-mono text-red-600">{r.breached || 0}</td>
              </tr>
            ))}
            {insights.techRows.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-10 text-center text-sm text-[#4A5568]">No tickets yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
